export function createCartEditHost({ root, importModule, navigate, fetchDocument, notify }) {
  const drawer = root.getElementById('cart-drawer');
  if (!drawer) return null;

  const track = drawer.querySelector('[data-cart-track]');
  const panel = drawer.querySelector('[data-edit-panel]');
  const cartPane = drawer.querySelector('[data-cart-pane]');
  const mountPoint = drawer.querySelector('[data-edit-mount]');
  const productLabel = drawer.querySelector('[data-edit-product-label]');
  const saveButton = drawer.querySelector('[data-action="edit-save"]');
  if (!track || !panel || !cartPane || !mountPoint || !saveButton) return null;

  // surface id -> module URL, emitted by the section from `asset_url` so no
  // theme path is ever hardcoded here.
  let surfaceModules = {};
  try {
    surfaceModules = JSON.parse(drawer.dataset.editModules || '{}');
  } catch {
    surfaceModules = {};
  }

  const mounters = new Map();
  const moduleImports = new Map();
  const documentCache = new Map();
  // `session` is the edit screen currently up; `openToken` identifies the OPEN
  // that created it. Both matter: the module import, the product fetch and the
  // mount are all async, so an open that is abandoned — back, Esc, or a second
  // line clicked — can still resolve afterwards. Without the token that late
  // resolution mounts a product into the new session, or navigates away from it.
  let session = null;
  let openToken = 0;

  const cartEdit = {
    register(surface, mount) {
      mounters.set(surface, mount);
    },
    loadProductDocument(productUrl) {
      // One fetch per product per session (~73KB gzipped, ~285ms): re-editing
      // the same line, or two lines of the same product, is then instant.
      if (!documentCache.has(productUrl)) {
        documentCache.set(
          productUrl,
          fetchDocument(productUrl).catch((error) => {
            // A failed fetch must not poison the cache — the next attempt has
            // to be able to retry rather than replay the rejection.
            documentCache.delete(productUrl);
            throw error;
          }),
        );
      }
      return documentCache.get(productUrl);
    },
  };

  // ── The slide ──────────────────────────────────────────────────────────────
  // `inert` is what actually takes the hidden screen out of play: it removes the
  // pane from the tab order AND from the accessibility tree, which `hidden`
  // cannot do here (display:none would kill the transition).
  //
  // Both moves are ordered the same way — enable the incoming pane, put focus
  // where it belongs, and only THEN inert the outgoing one. Marking a pane inert
  // while focus is still inside it blurs to <body>, and that blur lands after a
  // synchronous focus() call, silently undoing it.
  //
  // `preventScroll` is not optional. Focusing the off-screen pane makes the
  // browser scroll it into view, which snaps the track's container sideways by a
  // full pane on the first frame — the transform then animates against an
  // already-scrolled box and jumps back when the content mounts.
  function slideToEdit() {
    panel.inert = false;
    track.dataset.state = 'edit';
    // The buyer asked to see this screen, not to start typing in it — and the
    // fields are hydrated a moment later from /cart.js.
    panel.focus({ preventScroll: true });
    cartPane.inert = true;
  }

  function slideToCart(focusTarget) {
    cartPane.inert = false;
    track.dataset.state = 'cart';
    if (focusTarget && typeof focusTarget.focus === 'function') focusTarget.focus({ preventScroll: true });
    panel.inert = true;
  }

  function setSaveState({ busy, canSave, error }) {
    saveButton.disabled = !canSave;
    saveButton.setAttribute('aria-busy', busy ? 'true' : 'false');
    saveButton.classList.toggle('cart-edit__save--busy', !!busy);
    if (error) saveButton.setAttribute('aria-describedby', 'cart-edit-error');
    else saveButton.removeAttribute('aria-describedby');
  }

  // ── Opening ────────────────────────────────────────────────────────────────
  /**
   * Resolve a surface's mounter, importing its module the first time.
   * Answers false when the surface has no module, or its module failed to load,
   * or it loaded without registering — every one of those means "navigate".
   */
  function ensureSurface(surface) {
    if (mounters.has(surface)) return Promise.resolve(true);
    const moduleUrl = surfaceModules[surface];
    if (!moduleUrl) return Promise.resolve(false);
    if (!moduleImports.has(surface)) {
      moduleImports.set(
        surface,
        importModule(moduleUrl).then(() => mounters.has(surface)).catch(() => false),
      );
    }
    return moduleImports.get(surface);
  }

  /** True while `token` still identifies the edit screen that is up. */
  const isCurrent = (token) => !!session && session.token === token;

  async function openEditor(link) {
    const surface = link.dataset.editSurface;
    const editKey = link.dataset.editKey;
    const productUrl = link.dataset.editUrl;

    await closeEditor({ focus: 'none' });
    const token = ++openToken;
    // Each screen gets its OWN element to mount into, and the protocol
    // listeners hang off that element rather than off the shared mount point.
    // The mounter's events do not bubble, so a save that resolves after its
    // screen is gone reaches a detached node with nothing listening — which is
    // the only guard that also holds when a SECOND screen is already open, and
    // `session` is therefore non-null but belongs to somebody else.
    const sessionRoot = root.createElement('div');
    sessionRoot.className = 'cart-edit__session';
    mountPoint.replaceChildren(sessionRoot);
    const line = link.closest('[data-cart-item]');
    const group = (line && line.dataset.group) || '';
    session = { destroy: null, opener: link, token, sessionRoot, group };
    attachProtocol(sessionRoot, token);
    if (productLabel) productLabel.textContent = link.dataset.editProduct || '';
    slideToEdit();
    setSaveState({ busy: true, canSave: false, error: '' });
    mountPoint.dataset.loading = 'true';

    // The slide already happened, so the module import and the product fetch
    // both run behind a screen the buyer can see — rather than behind a link
    // that looks dead for as long as they take.
    //
    // Every resumption below re-checks the token, because all three awaits can
    // outlive their own screen: going back and opening a second line while the
    // first is still loading would otherwise mount that first product into the
    // second screen, or navigate the page out from under it.
    try {
      const ready = await ensureSurface(surface);
      if (!isCurrent(token)) return;
      const mount = ready && mounters.get(surface);
      if (!mount) throw new Error('cart-edit: no mounter for ' + surface);
      const handle = await mount({ root: sessionRoot, editKey, productUrl });
      if (!isCurrent(token)) {
        if (handle && typeof handle.destroy === 'function') handle.destroy();
        return;
      }
      session.destroy = handle && handle.destroy;
    } catch {
      if (!isCurrent(token)) return;
      // Nothing usable mounted — fall back to the link's own href, which is the
      // full-page `?edit=` editor this panel is an optimisation over.
      await closeEditor({ focus: 'none' });
      navigate(link.href);
    } finally {
      if (isCurrent(token) || !session) mountPoint.removeAttribute('data-loading');
    }
  }

  /**
   * Leave the edit screen.
   *
   * `focus` — where to put focus on the way out:
   *   'opener'  back on the "Editar personalização" link (abandoning the edit)
   *   'cart'    the top of the bag (after a save: the edited line was REPLACED,
   *             so its link — and its key — no longer exist to return to)
   *   'none'    leave focus alone (the drawer itself is closing)
   */
  async function closeEditor({ focus = 'opener' } = {}) {
    if (!session) return;
    const { destroy, opener } = session;
    session = null;
    let focusTarget = null;
    if (focus === 'opener') focusTarget = root.contains(opener) ? opener : cartPane;
    else if (focus === 'cart') focusTarget = cartPane;
    slideToCart(focusTarget);
    if (typeof destroy === 'function') destroy();
    // Detaching this screen's element is what silences anything its mounter
    // still has in flight; the next open builds its own.
    mountPoint.replaceChildren();
  }

  // ── Wiring ─────────────────────────────────────────────────────────────────
  // Delegated on the persistent <dialog>, so it survives the cart list being
  // re-rendered under it.
  drawer.addEventListener('click', (event) => {
    const link = event.target.closest('[data-edit-surface]');
    if (link && drawer.contains(link)) {
      const surface = link.dataset.editSurface;
      // A surface this drawer knows nothing about keeps its href: that link
      // navigates to the PDP's `?edit=` editor, which is the whole no-JS and
      // not-yet-ported path. Anything else is ours, even before its module has
      // loaded — openEditor() waits for it behind the opened screen.
      if (!mounters.has(surface) && !surfaceModules[surface]) return;
      event.preventDefault();
      openEditor(link);
      return;
    }
    if (event.target.closest('[data-action="edit-back"]')) {
      event.preventDefault();
      closeEditor();
    }
  });

  // Warm the module on pointer-down so the wait above is usually already over
  // by the time the click lands.
  drawer.addEventListener('pointerdown', (event) => {
    const link = event.target.closest('[data-edit-surface]');
    if (link) ensureSurface(link.dataset.editSurface);
  });

  saveButton.addEventListener('click', () => {
    if (!session) return;
    session.sessionRoot.dispatchEvent(new CustomEvent('fn:edit-save'));
  });

  /**
   * Listen for one screen's half of the protocol, on that screen's own element.
   *
   * The token check is belt to the element's braces: the element alone already
   * isolates screens, and the token makes a stale event impossible to act on
   * even if a mounter were to reuse a root it was handed earlier.
   */
  function attachProtocol(sessionRoot, token) {
    sessionRoot.addEventListener('fn:edit-state', (event) => {
      if (!isCurrent(token)) return;
      setSaveState(event.detail || {});
    });

    sessionRoot.addEventListener('fn:edit-saved', async (event) => {
      if (!isCurrent(token)) return;
      const sections = (event.detail || {}).sections;
      const { group } = session;
      // Re-render the bag FIRST, so focus lands in a list that already shows the
      // saved values. The drawer's own listener owns that swap; with the dialog
      // already open, its showModal() guard makes this a content swap, not a reopen.
      // No `sections` in the payload means the mounter could not supply the
      // rendered bag, and the listener falls back to refreshing from the server.
      root.dispatchEvent(new CustomEvent('cart:open', { detail: { sections } }));
      await closeEditor({ focus: 'cart' });
      notify({
        title: drawer.dataset.noticeUpdated || '',
        subtitle: productLabel ? productLabel.textContent : '',
        group,
      });
    });
  }

  // Esc closes the top thing, not the whole drawer. Captured before the dialog's
  // own default so the <dialog> never sees it while the edit screen is up.
  drawer.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !session) return;
    event.preventDefault();
    event.stopPropagation();
    closeEditor();
  }, true);

  // Closing the drawer entirely abandons the edit screen, so the next open
  // starts on the bag.
  drawer.addEventListener('close', () => { closeEditor({ focus: 'none' }); });

  return cartEdit;
}

// ── Page entry point ────────────────────────────────────────────────────────
// Skipped when there is no document (the test runner), so importing this module
// under `node --test` yields the factory alone.
if (typeof document !== 'undefined') {
  const host = createCartEditHost({
    root: document,
    importModule: (url) => import(url),
    fetchDocument: (url) => fetch(url, { headers: { Accept: 'text/html' } })
      .then((response) => {
        if (!response.ok) throw new Error('cart-edit: product fetch failed');
        return response.text();
      })
      .then((markup) => new DOMParser().parseFromString(markup, 'text/html')),
    navigate: (url) => window.location.assign(url),
    notify: (payload) => {
      if (window.fnCartNotice && payload.title) window.fnCartNotice.show(payload);
    },
  });
  if (host) {
    window.fnCartEdit = host;
    // Announced after the global is in place: a surface module that loaded
    // before this one waits for exactly this event to register itself.
    document.dispatchEvent(new CustomEvent('fn:cart-edit-ready'));
  }
}
